import { useEffect, useRef, useState } from 'react';
import { Icon } from '../primitives/Icon.tsx';
import { IconButton } from '../primitives/IconButton.tsx';
import './copy-field.css';

export interface CopyFieldProps {
  label: string;
  value: string;
  /** Narrower field for a short value such as a discount code. */
  size?: 'full' | 'compact';
}

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * A read-only value with a copy button.
 *
 * The input is real and selectable, so copying still works when the Clipboard API does
 * not — it is unavailable on insecure origins and can be refused outright, which is
 * exactly the case a copy-only control would leave a partner stuck in.
 */
export function CopyField({ label, value, size = 'full' }: CopyFieldProps) {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);

  // Without this the timeout fires after unmount and React warns; more to the point a
  // field copied and then navigated away from would leak a timer per copy.
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      // Select the text instead, so the keyboard shortcut is one keystroke away.
      input.current?.select();
      setState('failed');
    }
    timer.current = setTimeout(() => setState('idle'), 3000);
  }

  const copied = state === 'copied';
  const message = state === 'failed' ? 'Press ⌘C or Ctrl+C to copy' : '';

  return (
    // No dark variant: the field resolves --surface and --on-surface from whichever
    // Surface contains it, so it is already correct on the inverted Refer panel.
    <div className="copyField" data-size={size}>
      <label className="copyFieldLabel" htmlFor={`copy-${slug(label)}`}>
        {label}
      </label>
      <div className="copyFieldRow">
        <input
          ref={input}
          id={`copy-${slug(label)}`}
          className="control controlInline"
          value={value}
          readOnly
          onFocus={(event) => event.currentTarget.select()}
        />
        {/* Filled, not outlined: copying is the only reason this field exists, and on
            the dark panel an outlined button all but disappears. */}
        <IconButton
          label={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}
          icon={<Icon name={copied ? 'check' : 'copy'} size={18} />}
          tone="accent"
          active={copied}
          onClick={() => void copy()}
        />
      </div>
      {/* Always present for failures, without layout shift under the button. */}
      <p className="copyFieldStatus" role="status" data-shown={state === 'failed' || undefined}>
        {message}
      </p>
    </div>
  );
}

function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
